import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '../../components/feature/PageLayout';
import PageHero from '../../components/feature/PageHero';
import PageSectionNav from '../../components/feature/PageSectionNav';

export default function ContactPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 3000);
    }, 1000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const leadershipContacts = [
    {
      name: 'His Grace Bishop Boulos',
      title: 'Bishop of Ottawa, Montreal & Eastern Canada',
      phones: ['(450) 400-7672 ext. 4', '(438) 862-6000'],
      email: 'hgboulos@eccopts.ca',
      image: '/assets/readdy/b073ceb25798d0e74cb37f9a44eef647-f69c779c3ae5.png'
    },
    {
      name: 'Father Anthony Micheal',
      title: 'Parish Priest',
      phones: ['(450) 400-7672 ext. 2', '(514) 947-6272'],
      email: 'franthony@smpcvi.ca',
      image: '/assets/readdy/f46f13081ecf4f52070ca15241409afa-e78d0b782d54.png'
    },
    {
      name: 'Osama Habshy',
      title: 'Parish Treasurer',
      phones: ['(450) 400-7672 ext. 3', '(514) 606-5670'],
      email: 'treasurer@smpcvi.ca',
      image: '/assets/readdy/41c4c49ed35ea235cdaf9de51bcb081d-61b7b0163acd.png'
    }
  ];



  return (
    <PageLayout hasHero>
      <PageHero 
        title={t('contactPage.title')}
        subtitle={t('contactPage.subtitle')}
        backgroundImage="/assets/readdy/442a8abb-782f-432f-b6c4-09b7eb30eb08_IMG_0444-eafb70ea2d69.jpeg"
      />

      <PageSectionNav
        sections={[
          { id: 'get-in-touch', label: t('contactPage.getInTouch') },
          { id: 'leadership-contacts', label: t('contactPage.leadership') },
        ]}
      />

      <section id="get-in-touch" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl font-serif font-bold text-gray-900 mb-8">{t('contactPage.getInTouch')}</h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gold-100 rounded-full flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-burgundy-700"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1 uppercase tracking-wider">{t('contactPage.address')}</h4>
                    <p className="text-gray-600">45 Boulevard du Curé-Labelle<br />Sainte-Thérèse, QC J7E 2X2</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gold-100 rounded-full flex-shrink-0">
                    <i className="ri-phone-line text-xl text-burgundy-700"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1 uppercase tracking-wider">{t('contactPage.phone')}</h4>
                    <p className="text-gray-600">(450) 400-7672 (SMPC)</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-gold-100 rounded-full flex-shrink-0">
                    <i className="ri-mail-line text-xl text-burgundy-700"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1 uppercase tracking-wider">{t('contactPage.email')}</h4>
                    <p className="text-gray-600">contact@smpcvi.ca</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mb-10">
                <a href="#" className="w-12 h-12 flex items-center justify-center border-2 border-burgundy-700 rounded-full text-burgundy-700 hover:bg-burgundy-700 hover:text-white transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 flex items-center justify-center border-2 border-burgundy-700 rounded-full text-burgundy-700 hover:bg-burgundy-700 hover:text-white transition-all cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 flex items-center justify-center border-2 border-burgundy-700 rounded-full text-burgundy-700 hover:bg-burgundy-700 hover:text-white transition-all cursor-pointer">
                  <i className="ri-youtube-fill text-xl"></i>
                </a>
                <a href="#" className="w-12 h-12 flex items-center justify-center border-2 border-burgundy-700 rounded-full text-burgundy-700 hover:bg-burgundy-700 hover:text-white transition-all cursor-pointer">
                  <i className="ri-twitter-x-line text-xl"></i>
                </a>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wider">{t('contactPage.findUs')}</h4>
                <div className="w-full h-64 rounded-xl overflow-hidden border-2 border-gray-200">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2788.8876!2d-73.8367!3d45.6389!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc922f0f0f0f0f0%3A0x0!2s45%20Boulevard%20du%20Cur%C3%A9-Labelle%2C%20Sainte-Th%C3%A9r%C3%A8se%2C%20QC%20J7E%202X2%2C%20Canada!5e0!3m2!1sen!2sca!4v1704067200000!5m2!1sen!2sca"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Church Location Map"
                  ></iframe>
                </div>
              </div>
            </div>

            <div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-xs font-semibold text-gray-900 mb-2 uppercase tracking-wider">
                    {t('contactPage.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-burgundy-700 focus:outline-none transition-colors text-sm"
                    placeholder={t('contactPage.namePlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-semibold text-gray-900 mb-2 uppercase tracking-wider">
                    {t('contactPage.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-burgundy-700 focus:outline-none transition-colors text-sm"
                    placeholder={t('contactPage.emailPlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs font-semibold text-gray-900 mb-2 uppercase tracking-wider">
                    {t('contactPage.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-burgundy-700 focus:outline-none transition-colors text-sm"
                    placeholder={t('contactPage.phonePlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-semibold text-gray-900 mb-2 uppercase tracking-wider">
                    {t('contactPage.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-burgundy-700 focus:outline-none transition-colors resize-none text-sm"
                    placeholder={t('contactPage.messagePlaceholder')}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-burgundy-700 text-white text-base font-semibold rounded-full hover:bg-burgundy-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? t('contactPage.sending') : t('contactPage.sendMessage')}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 text-sm text-center">
                    {t('contactPage.thankYou')}
                  </div>
                )}

                <p className="text-xs text-gray-500 text-center">
                  {t('contactPage.privacyNote')}
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Contacts Section */}
      <section id="leadership-contacts" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-gray-900 mb-3">{t('aboutPage.churchBoard')}</h2>
            <p className="text-gray-600">{t('aboutPage.churchBoardDesc')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {leadershipContacts.map((contact, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="w-full h-64 overflow-hidden">
                  <img
                    src={contact.image}
                    alt={contact.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{contact.name}</h3>
                  <p className="text-sm text-burgundy-700 font-medium mb-4">{contact.title}</p>
                  <div className="space-y-2">
                    {contact.phones && contact.phones.map((phone, phoneIndex) => (
                      <a key={phoneIndex} href={`tel:${phone.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-2 text-gray-600 hover:text-burgundy-700 transition-colors cursor-pointer">
                        <i className="ri-phone-line text-lg"></i>
                        <span className="text-sm">{phone}</span>
                      </a>
                    ))}
                    <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-gray-600 hover:text-burgundy-700 transition-colors cursor-pointer">
                      <i className="ri-mail-line text-lg"></i>
                      <span className="text-sm">{contact.email}</span>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resources CTA */}
      <section className="py-20 bg-gradient-to-br from-[#3d1a1e] to-[#2a1215]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-6">
            <i className="ri-book-open-line text-3xl text-amber-400"></i>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">{t('nav.resources')}</h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            {t('contactPage.resourcesCTA')}
          </p>
          <a
            href="/resources"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-amber-500 text-[#3d1a1e] font-semibold rounded-full hover:bg-amber-400 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
          >
            <span>{t('contactPage.goToResources')}</span>
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>

    </PageLayout>
  );
}
